import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { BellIcon } from '../../components/BellIcon';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const formatMoney = (value) => `${Number(value || 0).toFixed(2)}€`;
const formatDate = (value) => {
  if (!value) return '--/--/----';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const buildStatementHtml = (statement) => {
  const rows = (statement.entries || []).map((entry) => `
    <tr>
      <td style="padding:10px;border-bottom:1px solid #eee;">${entry.source_label}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;">${entry.guest_name || entry.listing_name || 'Allure host payout'}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;">${formatDate(entry.occurred_at)}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">${formatMoney(entry.total_amount)}</td>
    </tr>
  `).join('');

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Allure Host Statement</title>
      <style>
        body { font-family: Georgia, serif; padding: 32px; color: #111; }
        table { width: 100%; border-collapse: collapse; margin-top: 18px; }
      </style>
    </head>
    <body>
      <h1>Allure Host Statement</h1>
      <p><strong>Month:</strong> ${statement.payout_month} · Batch ${statement.batch_sequence}</p>
      <p><strong>Host:</strong> ${statement.host?.name || 'Host'} (${statement.host?.email || ''})</p>
      <p><strong>Status:</strong> ${statement.status}</p>
      <p><strong>Total:</strong> ${formatMoney(statement.summary?.total_amount)}</p>
      <p><strong>Commission:</strong> ${formatMoney(statement.summary?.commission_total)} · <strong>Host tips:</strong> ${formatMoney(statement.summary?.host_tips_total)} · <strong>Rewards:</strong> ${formatMoney(statement.summary?.reward_total)}</p>
      <table>
        <thead>
          <tr>
            <th style="text-align:left;padding:10px;border-bottom:2px solid #111;">Type</th>
            <th style="text-align:left;padding:10px;border-bottom:2px solid #111;">Guest / listing</th>
            <th style="text-align:left;padding:10px;border-bottom:2px solid #111;">Date</th>
            <th style="text-align:right;padding:10px;border-bottom:2px solid #111;">Amount</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
  </html>`;
};

export default function HostPayoutStatementPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const { batchId } = useParams();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatement = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/host/payouts/statements/${batchId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatement(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Unable to load this statement');
      } finally {
        setLoading(false);
      }
    };

    fetchStatement();
  }, [batchId]);

  const statementHtml = useMemo(() => (statement ? buildStatementHtml(statement) : ''), [statement]);

  const handleDownload = () => {
    if (!statementHtml || !statement?.download_filename) return;
    const blob = new Blob([statementHtml], { type: 'text/html' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = statement.download_filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="app-shell" data-testid="host-payout-statement-page" style={{ backgroundColor: 'var(--app-bg)', minHeight: '100vh', paddingBottom: 32 }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button data-testid="host-payout-statement-back-button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={34} color="var(--text)" strokeWidth={1.8} />
        </button>
        <button data-testid="host-payout-statement-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 16px', gap: 14 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 144, height: 126, objectFit: 'contain', marginTop: 8 }} />
        <h1 style={{ fontFamily: font, fontSize: 30, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Monthly statement</h1>

        <div style={{ ...hostCardStyle, width: '100%', maxWidth: 360, display: 'grid', gap: 12 }}>
          {loading ? <p style={{ margin: 0, color: 'var(--text-2)' }}>Loading statement…</p> : null}
          {!loading && statement ? (
            <>
              <p data-testid="host-payout-statement-title" style={{ margin: 0, fontFamily: font, fontSize: 22, fontWeight: 700 }}>{statement.payout_month} · Batch {statement.batch_sequence}</p>
              <p style={{ margin: 0, fontFamily: font, fontSize: 16, color: 'var(--text-2)' }}>{statement.host?.name} · {statement.host?.email}</p>
              <p data-testid="host-payout-statement-total" style={{ margin: 0, fontFamily: font, fontSize: 28, fontWeight: 700, color: '#04A628' }}>{formatMoney(statement.summary?.total_amount)}</p>
              <p style={{ margin: 0, fontFamily: font, fontSize: 15, color: 'var(--text-2)' }}>Commission {formatMoney(statement.summary?.commission_total)} · Host tips {formatMoney(statement.summary?.host_tips_total)} · Rewards {formatMoney(statement.summary?.reward_total)}</p>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" data-testid="host-payout-statement-download-button" onClick={handleDownload} style={{ flex: 1, border: 'none', borderRadius: 14, padding: '12px 14px', background: '#111', color: '#FFF', cursor: 'pointer', fontFamily: font, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Download size={18} /> Download
                </button>
                <button type="button" data-testid="host-payout-statement-print-button" onClick={() => window.print()} style={{ flex: 1, border: '1px solid #E7E7E7', borderRadius: 14, padding: '12px 14px', background: '#FFF', color: '#111', cursor: 'pointer', fontFamily: font, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Printer size={18} /> Print
                </button>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                {(statement.entries || []).map((entry) => (
                  <div key={entry.id} data-testid={`host-payout-statement-entry-${entry.id}`} style={{ borderTop: '1px solid #F1F1F1', paddingTop: 10 }}>
                    <p style={{ margin: 0, fontFamily: font, fontSize: 18, fontWeight: 700 }}>{entry.source_label}</p>
                    <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 14, color: 'var(--text-2)' }}>{entry.guest_name || entry.listing_name || 'Allure host payout'} · {formatDate(entry.occurred_at)}</p>
                    <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 16, fontWeight: 700 }}>{formatMoney(entry.total_amount)}</p>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}